import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import services from '../data/services.jsx';
import { Helmet } from 'react-helmet-async';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s =>
    s.title.toLowerCase().replace(/\s+/g, '-') === slug
  );

  if (!service) {
    return (
      <div className="container py-24 text-center">
        <Helmet>
          <title>Service Not Found | Nayrix</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 className="text-3xl font-bold mb-4">Service Not Found</h1>
        <Link to="/services" className="text-primary-600 underline">Back to Services</Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{service.title} | Nayrix Services</title>
        <meta name="description" content={service.description} />
        <meta property="og:title" content={`${service.title} | Nayrix Services`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://nayrix.com/services/${slug}`} />
        <link rel="canonical" href={`https://nayrix.com/services/${slug}`} />
      </Helmet>
      <section className="py-24 dark:bg-dark-bg bg-light-bg">
        <div className="container px-4 mx-auto">
          <nav className="mb-8 text-sm" aria-label="Breadcrumb">
            <ol className="list-reset flex text-neutral-500 dark:text-neutral-400">
              <li><Link to="/" className="hover:text-primary-600">Home</Link></li>
              <li className="mx-2">/</li>
              <li><Link to="/services" className="hover:text-primary-600">Services</Link></li>
              <li className="mx-2">/</li>
              <li className="text-primary-600 dark:text-primary-400 font-semibold">{service.title}</li>
            </ol>
          </nav>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex justify-center mb-6">{service.icon}</div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 dark:text-dark-text text-light-text">
              {service.title}
            </h1>
            <p className="text-xl dark:text-dark-text-secondary text-light-text-secondary max-w-3xl mx-auto">
              {service.description}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <article className="p-6 rounded-xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border/50 border-light-border/50">
              <h2 className="text-xl font-semibold mb-4 dark:text-dark-text text-light-text">Key Features</h2>
              <ul className="space-y-3">
                {service.details.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center dark:text-dark-text-secondary text-light-text-secondary">
                    <svg className="w-5 h-5 mr-3 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </article>
            <article className="p-6 rounded-xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border/50 border-light-border/50">
              <h2 className="text-xl font-semibold mb-4 dark:text-dark-text text-light-text">Benefits</h2>
              <ul className="space-y-3">
                {service.details.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-center dark:text-dark-text-secondary text-light-text-secondary">
                    <svg className="w-5 h-5 mr-3 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </article>
          </div>
          <div className="mb-8 p-6 rounded-xl dark:bg-dark-bg/50 bg-light-bg/50 border dark:border-dark-border/50 border-light-border/50">
            <h2 className="text-xl font-semibold mb-4 dark:text-dark-text text-light-text">Technologies</h2>
            <div className="flex flex-wrap gap-2">
              {service.details.technologies.map((tech, idx) => (
                <span key={idx} className="px-4 py-2 rounded-full dark:bg-dark-accent/10 bg-light-accent/10 dark:text-dark-accent text-light-accent text-sm border dark:border-dark-border/30 border-light-border/30">
                  {tech}
                </span>
              ))}
            </div>
          </div>
          <div className="text-center mt-8">
            <Link to="/contact" className="px-8 py-3 rounded-full dark:bg-dark-accent hover:bg-dark-accent-hover bg-light-accent hover:bg-light-accent-hover text-white transition-all duration-300 inline-block hover:scale-105 hover:shadow-lg">
              Get Started
            </Link>
            <Link to="/services" className="ml-4 px-8 py-3 rounded-full border dark:border-dark-border border-light-border text-light-text dark:text-dark-text transition-all duration-300 inline-block hover:scale-105 hover:shadow-lg">
              Back to Services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceDetail;